import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Play,
  Sparkles,
  Star,
  Code2,
  Smartphone,
  Globe,
  Search,
  Megaphone,
  Link2,
  Server,
  Building2,
  ShoppingCart,
  Database,
  Cpu,
  Rocket,
  Cloud,
  Palette,
  ShieldCheck,
  BarChart3,
  Bot,
  Mail,
  CreditCard,
  Layers,
  GitBranch,
  Figma,
} from "lucide-react";
import vision2030Img from "@/assets/badges/vision2030.svg";
import zatcaImg from "@/assets/badges/zatca.svg";
import fatoraImg from "@/assets/badges/fatora.svg";
import { fetchBrandLogos } from "@/lib/brand-logos";

const STATIC_BADGES: Record<string, { src: string; alt: string; label: string }> = {
  vision2030: { src: vision2030Img, alt: "Saudi Vision 2030", label: "Vision 2030" },
  zatca: { src: zatcaImg, alt: "ZATCA Compliant", label: "ZATCA" },
  fatora: { src: fatoraImg, alt: "Fatoora Compliant", label: "Fatoora" },
};

function ComplianceBadges() {
  const { data: dbLogos } = useQuery({
    queryKey: ["brand_logos", "public"],
    queryFn: () => fetchBrandLogos(false),
    staleTime: 60_000,
  });

  const badges = (dbLogos && dbLogos.length > 0)
    ? dbLogos.map((l) => {
        const fallback = STATIC_BADGES[l.key];
        return {
          key: l.key,
          src: l.image_url || fallback?.src || "",
          alt: l.alt || l.name,
          label: l.name,
        };
      }).filter((b) => b.src)
    : Object.entries(STATIC_BADGES).map(([key, b]) => ({ key, ...b }));

  return (
    <div className="mt-8 flex flex-col gap-2">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        Trusted & Compliant
      </span>
      <div className="flex flex-wrap items-center gap-3">
        {badges.map((b) => (
          <div
            key={b.key}
            title={b.alt}
            className="group flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
          >
            <img
              src={b.src}
              alt={b.alt}
              loading="lazy"
              decoding="async"
              width={120}
              height={40}
              className="h-10 w-auto max-w-[120px] object-contain sm:h-11 sm:max-w-[130px]"
            />
          </div>
        ))}
      </div>
    </div>
  );
}


const TYPING_WORDS = [
  "Web Development",
  "Mobile Apps",
  "SaaS Platforms",
  "ERP & POS",
  "AI Automation",
  "Hosting & Domains",
  "Digital Marketing",
];

function TypingService() {
  const [wordIdx, setWordIdx] = useState(0);
  const [text, setText] = useState(TYPING_WORDS[0]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = TYPING_WORDS[wordIdx];
    let delay = deleting ? 45 : 95;

    if (!deleting && text === current) {
      const t = setTimeout(() => setDeleting(true), 1800);
      return () => clearTimeout(t);
    }
    if (deleting && text === "") {
      setDeleting(false);
      setWordIdx((i) => (i + 1) % TYPING_WORDS.length);
      return;
    }

    const t = setTimeout(() => {
      setText((prev) =>
        deleting ? current.slice(0, prev.length - 1) : current.slice(0, prev.length + 1)
      );
    }, delay);
    return () => clearTimeout(t);
  }, [text, deleting, wordIdx]);

  return (
    <span className="relative inline-flex items-baseline whitespace-nowrap text-gradient">
      <span>{text}</span>
      <span
        aria-hidden="true"
        className="ml-1 inline-block h-[0.85em] w-[2px] translate-y-[2px] bg-primary animate-pulse select-none"
      />
    </span>
  );
}

export function Hero() {
  return (
    <section className="relative overflow-hidden pt-8 pb-20 lg:pt-10 lg:pb-28">
      <div className="absolute inset-0 -z-10 bg-hero-glow" />
      <div className="absolute inset-0 -z-10 grid-bg opacity-60" />

      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* Left: text */}
        <div className="text-left">
          <div className="inline-flex items-center gap-2 rounded-full border border-border glass px-3 py-1 text-xs font-medium text-muted-foreground animate-fade-in">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            AI-powered digital agency
            <span className="mx-1 text-border">•</span>
            Trusted by 200+ brands
          </div>

          <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl text-foreground">
            We build{" "}
            <span className="block min-h-[1.2em]">
              <TypingService />
            </span>
            <span className="block text-foreground/90">that ships & scales.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-foreground/80 dark:text-muted-foreground">
            From SaaS and ERP to POS, hosting and AI automation — CoderLity is the
            full-stack partner ambitious teams trust to ship faster and grow smarter.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow hover:opacity-95 transition">
              Start a project
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
            </Link>
            <Link to="/portfolio" className="inline-flex items-center gap-2 rounded-full border border-border glass px-6 py-3 text-sm font-semibold text-foreground hover:bg-secondary transition">
              <Play className="h-4 w-4 text-primary" />
              See our work
            </Link>
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
            </div>
            4.9/5 from 320+ reviews
          </div>

          <ComplianceBadges />
        </div>

        {/* Right: orbiting tech & services (desktop only for lightning mobile performance) */}
        <div
          className="hidden lg:block relative mx-auto aspect-square w-full max-w-[380px] lg:ml-auto lg:mr-[10px] lg:-mt-4 animate-fade-in"
        >
          <OrbitingTech />
        </div>
      </div>

      {/* Scroll-down indicator */}
      <ScrollDownCue />
    </section>
  );
}

function ScrollDownCue() {
  const handleClick = () => {
    const hero = document.querySelector("section");
    const next = hero?.nextElementSibling as HTMLElement | null;
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollBy({ top: window.innerHeight * 0.9, behavior: "smooth" });
  };

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center animate-fade-in">
      <button
        type="button"
        onClick={handleClick}
        aria-label="Scroll down"
        className="pointer-events-auto group relative flex flex-col items-center gap-2 outline-none"
      >
        {/* Soft glow halo behind the mouse */}
        <span className="absolute -top-3 left-1/2 h-16 w-28 -translate-x-1/2 rounded-[50%] bg-primary/25 blur-2xl" />

        {/* Mouse shape */}
        <span className="relative flex h-11 w-7 items-start justify-center rounded-full border border-primary/60 bg-gradient-to-b from-primary/30 to-primary/5 shadow-glow backdrop-blur transition group-hover:border-primary group-hover:from-primary/40">
          <span className="mt-1.5 block h-1.5 w-1.5 rounded-full bg-primary shadow-glow animate-bounce" />
        </span>

        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/90 group-hover:text-primary transition">
          Scroll Down
        </span>
      </button>
    </div>
  );
}

type OrbitIcon = { icon: typeof Code2; label: string; color: string };

const OUTER_ICONS: OrbitIcon[] = [
  { icon: Link2, label: "Domain", color: "text-sky-400" },
  { icon: Server, label: "Hosting", color: "text-indigo-400" },
  { icon: Code2, label: "Code", color: "text-violet-400" },
  { icon: Smartphone, label: "App", color: "text-pink-400" },
  { icon: Globe, label: "Web", color: "text-cyan-400" },
  { icon: Search, label: "SEO", color: "text-emerald-400" },
  { icon: Megaphone, label: "Marketing", color: "text-amber-400" },
  { icon: Building2, label: "ERP", color: "text-fuchsia-400" },
  { icon: ShoppingCart, label: "POS", color: "text-rose-400" },
  { icon: Cloud, label: "Cloud", color: "text-sky-300" },
  { icon: ShieldCheck, label: "Security", color: "text-emerald-500" },
  { icon: BarChart3, label: "Analytics", color: "text-blue-400" },
  { icon: Bot, label: "AI", color: "text-purple-400" },
  { icon: Mail, label: "Email", color: "text-orange-400" },
  { icon: CreditCard, label: "Payments", color: "text-teal-400" },
  { icon: Palette, label: "Design", color: "text-pink-300" },
];

const INNER_ICONS: OrbitIcon[] = [
  { icon: Cpu, label: "Node.js", color: "text-emerald-400" },
  { icon: Rocket, label: "Next.js", color: "text-foreground" },
  { icon: Database, label: "MongoDB", color: "text-green-500" },
  { icon: Layers, label: "React", color: "text-cyan-400" },
  { icon: GitBranch, label: "Git", color: "text-orange-500" },
  { icon: Figma, label: "Figma", color: "text-pink-400" },
];

function OrbitingTech() {
  return (
    <div className="relative h-full w-full">
      {/* Glow */}
      <div className="absolute inset-0 -z-10 rounded-full bg-gradient-brand opacity-25 blur-3xl" />

      {/* Orbit rings */}
      <div className="absolute inset-0 rounded-full border border-border/60" />
      <div className="absolute inset-[18%] rounded-full border border-border/50" />
      <div className="absolute inset-[36%] rounded-full border border-dashed border-border/50" />

      {/* Outer orbit (clockwise) */}
      <div className="absolute inset-0 animate-spin-cw">
        {OUTER_ICONS.map((it, i) => {
          const angle = (i / OUTER_ICONS.length) * 2 * Math.PI;
          const r = 50; // %
          const x = 50 + r * Math.cos(angle);
          const y = 50 + r * Math.sin(angle);
          const Icon = it.icon;
          return (
            <div
              key={it.label}
              className="absolute -translate-x-1/2 -translate-y-1/2 animate-spin-ccw"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="group flex flex-col items-center gap-0.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border glass shadow-card transition group-hover:scale-110">
                  <Icon className={`h-4 w-4 ${it.color}`} />
                </div>
                <span className="text-[9px] font-medium text-muted-foreground">{it.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Inner orbit (counter-clockwise) */}
      <div className="absolute inset-[18%] animate-spin-inner-ccw">
        {INNER_ICONS.map((it, i) => {
          const angle = (i / INNER_ICONS.length) * 2 * Math.PI - Math.PI / 2;
          const r = 50;
          const x = 50 + r * Math.cos(angle);
          const y = 50 + r * Math.sin(angle);
          const Icon = it.icon;
          return (
            <div
              key={it.label}
              className="absolute -translate-x-1/2 -translate-y-1/2 animate-spin-inner-cw"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border glass shadow-card">
                <Icon className={`h-4 w-4 ${it.color}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Center: coding + marketing */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-brand shadow-glow animate-pulse">
          <div className="absolute inset-1 rounded-full bg-background/80 backdrop-blur" />
          <div className="relative flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-primary" />
              <Megaphone className="h-4 w-4 text-pink-400" />
            </div>
            <span className="text-[8px] font-semibold tracking-wide text-foreground/80">
              CODE · GROW
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
